import { FetchError } from 'ofetch'
import { navigateTo } from '#imports'
import type { PaAPI, UnleashFeatureFlag } from '@rialtic/types'
import { acceptHMRUpdate, defineStore } from 'pinia'

let abortController: AbortController | null = null

export const useAppConfigurationUnleashed = defineStore(
  'app-config-unleashed',
  {
    state: () => ({
      featureFlags: [] as UnleashFeatureFlag[],
      isInitialized: false,
    }),

    actions: {
      async getAll() {
        if (import.meta.server || this.isInitialized) return this.$state
        abortController?.abort()
        abortController = new AbortController()
        const { signal } = abortController

        try {
          const { $groundcover, $auth0, $auth0Ready } = useNuxtApp()
          const auth0Client = $auth0()
          await $auth0Ready()

          if (!auth0Client.isAuthenticated.value) {
            if (import.meta.client) {
              await navigateTo('/auth/login')
            }
            return this.$state
          }

          const apiFetch = $apiFetcher()

          try {
            const data = await apiFetch<PaAPI.Get['/features']>(
              '/api/paapi/features',
              { signal },
            )
            this.featureFlags = data as UnleashFeatureFlag[]
            this.isInitialized = true
          } catch (error) {
            if ((error as Error).name === 'AbortError') return this.$state

            const shouldRedirect =
              error instanceof FetchError &&
              [400, 401].includes(error.status ?? 0)

            if (shouldRedirect && import.meta.client) {
              await navigateTo('/auth/login')
              return this.$state
            }

            $groundcover?.captureException(error as Error, {
              function: 'stores/appConfig:getAllUnleash',
            })
          }
        } finally {
          abortController = null
        }

        return this.$state
      },
    },

    getters: {
      isUnleashFeatureEnabled: (state) => {
        return (featureName: string): boolean => {
          return state.featureFlags?.length
            ? state.featureFlags.some(
                (feature: UnleashFeatureFlag) =>
                  feature.name === featureName && feature.enabled,
              )
            : false
        }
      },
    },
  },
)

if (import.meta.hot)
  import.meta.hot.accept(
    acceptHMRUpdate(useAppConfigurationUnleashed, import.meta.hot),
  )
